import { all, call, put, retry, select, takeLatest } from 'redux-saga/effects';

import { followersActions, followersForUserSelector } from '$store/followers/index';
import { Api } from '$api';
import {
  DeleteFollowerAction,
  LoadAction,
  LoadMoreAction,
} from '$store/followers/interface';
import { currentUserSelector, mainActions } from '$store/main';
import { RootState } from '$store/rootReducer';
import { FollowAction, FollowFromAdsAction } from '$store/groups/interface';
import { profileActions } from '$store/profile';
import { groupsActions } from '$store/groups';
import { batchActions } from '$store';
import router from '$navigation/router';
import { toastActions } from '$store/toast';
import { statReachGoal } from '$utils';
import { reelsActions } from '$store/reels';

function* loadWorker(action: LoadAction) {
  try {
    const userId = action.payload;
    const query = router.getState().params?.query ?? '';
    const resp = yield retry(Infinity, 3000, Api.post, `/followers/${userId}`, {
      query: query.trim(),
    });
    yield put(
      batchActions(
        mainActions.setUsers(resp.users),
        mainActions.setGroups(resp.groups),
        followersActions.setResult({
          userId,
          inbox: resp.inbox,
          outbox: resp.outbox,
          inboxNextFrom: resp.inboxNextFrom,
          outboxNextFrom: resp.outboxNextFrom,
        }),
        followersActions.setFollowing(resp.followed),
      ),
    );
  } catch (e) {}
}

function* loadMoreWorker(action: LoadMoreAction) {
  try {
    const { userId, tab } = action.payload;
    const { inboxNextFrom, outboxNextFrom } = yield select((state: RootState) =>
      followersForUserSelector(state, userId),
    );
    const nextFrom = tab === 'inbox' ? inboxNextFrom : outboxNextFrom;
    const query = router.getState().params?.query ?? '';

    const resp = yield call(Api.post, `/followers/${userId}/more`, {
      startFrom: +nextFrom,
      type: tab,
      query,
    });

    yield put(
      batchActions(
        mainActions.setUsers(resp.users),
        mainActions.setGroups(resp.groups),
        followersActions.setMoreResult({
          userId,
          tab,
          items: resp.items,
          nextFrom: resp.nextFrom,
        }),
        followersActions.setFollowing(resp.followed),
      ),
    );
  } catch (e) {}
}

function* followFromAdsWorker(action: FollowFromAdsAction) {
  const { toId, adsId } = action.payload;
  try {
    yield put(
      batchActions(
        followersActions.setFollowing({
          [toId]: true,
        }),
        profileActions.setFollowStatus({ userId: toId, isFollowed: true }),
      ),
    );
    const resp = yield call(Api.post, `/follow/${toId}/add?ad_id=${adsId}`);
    yield put(
      batchActions(
        mainActions.setUser(resp.user),
        followersActions.setFollowing({
          [toId]: true,
        }),
      ),
    );
    yield call(statReachGoal, 'follow');
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
    yield put(
      batchActions(
        followersActions.setFollowing({
          [toId]: false,
        }),
        profileActions.setFollowStatus({ userId: toId, isFollowed: false }),
      ),
    );
  }
}

function* followWorker(action: FollowAction) {
  if (action.payload > 0) {
    yield put(profileActions.follow({ id: action.payload }));
  } else {
    yield put(groupsActions.follow(-action.payload));
  }
}

function* unfollowWorker(action: FollowAction) {
  if (action.payload > 0) {
    yield put(reelsActions.deleteUserFollow(action.payload));
    yield put(profileActions.unfollow(action.payload));
  } else {
    yield put(groupsActions.unfollow(-action.payload));
  }
}

function* loadBirthdaysWorker() {
  try {
    const data = yield call(Api.get, '/followers/birthdays');
    yield put(
      batchActions(
        mainActions.setUsers(data.users),
        followersActions.setBirthdaysLoaded({
          userIds: Object.keys(data.users).map((item) => +item),
        }),
      ),
    );
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
    yield put(followersActions.setBirthdaysFailed());
  }
}

function* deleteFollowerWorker(action: DeleteFollowerAction) {
  const { deletedUserId, currentUserId } = action.payload;
  const currentUser = yield select(currentUserSelector);
  const { inbox } = yield select(followersForUserSelector, currentUserId);

  try {
    const filteredFollowers = inbox.filter((user) => user.objectId !== deletedUserId);

    yield put(toastActions.setToastLoading());
    yield call(Api.get, `/followers/${deletedUserId}/delete`);
    yield put(
      batchActions(
        followersActions.setFollowers({
          type: 'inbox',
          data: filteredFollowers,
          id: currentUserId,
        }),
        mainActions.setUser({
          ...currentUser,
          followers: currentUser.followers - 1,
        }),
      ),
    );
    yield put(toastActions.hideToast());
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

export function* followersSaga() {
  yield all([
    takeLatest(followersActions.load, loadWorker),
    takeLatest(followersActions.loadMore, loadMoreWorker),
    takeLatest(followersActions.follow, followWorker),
    takeLatest(followersActions.followFromAds, followFromAdsWorker),
    takeLatest(followersActions.unfollow, unfollowWorker),
    takeLatest(followersActions.loadBirthdays, loadBirthdaysWorker),
    takeLatest(followersActions.deleteFollower, deleteFollowerWorker),
  ]);
}
